import { Observable } from 'rxjs';
import { ApiResponse, CampaignFileUploadResponse } from '../../../shared/types';

/** FormData field name for landing/success page logo uploads. */
export const LANDING_PAGE_LOGO_FIELD = 'logo_landing_page';

const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
const ALLOWED_LOGO_EXTENSIONS = ['.png', '.jpg', '.jpeg'];

export function isAllowedLogoImage(file: File): boolean {
  if (ALLOWED_LOGO_TYPES.includes(file.type)) {
    return true;
  }
  const lowerName = file.name.toLowerCase();
  return ALLOWED_LOGO_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
}

export function validateLogoFileSize(file: File, maxBytes: number): boolean {
  return file.size <= maxBytes;
}

export function validateImageDimensions(
  file: File,
  maxWidth: number,
  maxHeight: number
): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img.width <= maxWidth && img.height <= maxHeight);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };

    img.src = url;
  });
}

export async function getLogoFileValidationError(
  file: File,
  maxWidth: number,
  maxHeight: number,
  maxBytes: number
): Promise<string> {
  if (!isAllowedLogoImage(file)) {
    return 'Only PNG, JPG, and JPEG files are allowed';
  }
  if (!validateLogoFileSize(file, maxBytes)) {
    const maxMb = Math.round(maxBytes / (1024 * 1024));
    return `File size must be ${maxMb}MB or less`;
  }

  const isValidDimensions = await validateImageDimensions(file, maxWidth, maxHeight);
  if (!isValidDimensions) {
    return `Image dimensions must not exceed ${maxWidth}x${maxHeight} pixels`;
  }

  return '';
}

/**
 * Reads uploaded logo URL from campaign upload-file API response.
 * Supports the named field, generic `url`, or a string `data` payload.
 */
export function extractCampaignLogoUploadUrl(
  response: ApiResponse<CampaignFileUploadResponse | string | Record<string, any>>,
  fieldName: string = LANDING_PAGE_LOGO_FIELD
): string {
  const data = response?.data;
  if (!data) {
    return '';
  }

  if (typeof data === 'string') {
    return data.trim();
  }

  const record = data as Record<string, any>;
  const fromField = record[fieldName];
  if (typeof fromField === 'string' && fromField.trim()) {
    return fromField.trim();
  }

  const url = record['url'];
  if (typeof url === 'string' && url.trim()) {
    return url.trim();
  }

  return '';
}

export function appendLandingPageLogoToFormData(formData: FormData, file: File): void {
  formData.append(LANDING_PAGE_LOGO_FIELD, file, file.name);
}

export function getDisplayNameForLogo(logoUrl: string): string {
  try {
    const pathname = new URL(logoUrl, window.location.origin).pathname;
    const segment = pathname.split('/').filter(Boolean).pop();
    return segment || 'Logo';
  } catch {
    return 'Logo';
  }
}

export function revokeBlobUrl(blobUrl: string): void {
  if (blobUrl?.startsWith('blob:')) {
    URL.revokeObjectURL(blobUrl);
  }
}

export type LogoUploadPrepareResult =
  | { status: 'noop' }
  | { status: 'invalid'; error: string; input: HTMLInputElement }
  | { status: 'ready'; file: File; blobUrl: string; fileName: string; input: HTMLInputElement };

export async function prepareLogoUploadFromEvent(
  event: Event,
  maxWidth: number,
  maxHeight: number,
  maxBytes: number
): Promise<LogoUploadPrepareResult> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) {
    return { status: 'noop' };
  }

  const validationError = await getLogoFileValidationError(file, maxWidth, maxHeight, maxBytes);
  if (validationError) {
    input.value = '';
    return { status: 'invalid', error: validationError, input };
  }

  return {
    status: 'ready',
    file,
    blobUrl: URL.createObjectURL(file),
    fileName: file.name,
    input
  };
}

export interface CampaignLogoUploadCallbacks {
  onSuccess: (logoUrl: string) => void;
  onFailure: () => void;
  setUploading: (uploading: boolean) => void;
  setValidationError: (message: string) => void;
  detectChanges: () => void;
  showUploadErrorToast?: () => void;
}

export function runCampaignLogoUpload(
  uploadFile: (formData: FormData) => Observable<ApiResponse<CampaignFileUploadResponse>>,
  file: File,
  input: HTMLInputElement,
  callbacks: CampaignLogoUploadCallbacks
): void {
  const formData = new FormData();
  appendLandingPageLogoToFormData(formData, file);

  uploadFile(formData).subscribe({
    next: (response) => {
      const logoUrl = extractCampaignLogoUploadUrl(response, LANDING_PAGE_LOGO_FIELD);
      if (logoUrl) {
        callbacks.onSuccess(logoUrl);
        callbacks.setValidationError('');
      } else {
        console.error('Logo upload failed: Invalid response structure', response);
        callbacks.setValidationError('Logo upload failed. Please try again.');
        callbacks.onFailure();
      }
      callbacks.setUploading(false);
      input.value = '';
      callbacks.detectChanges();
    },
    error: (error) => {
      console.error('Error uploading logo:', error);
      callbacks.setValidationError('Failed to upload logo. Please try again.');
      callbacks.showUploadErrorToast?.();
      callbacks.onFailure();
      callbacks.setUploading(false);
      input.value = '';
      callbacks.detectChanges();
    }
  });
}

export interface ModalLogoUploadConfig {
  maxWidth: number;
  maxHeight: number;
  maxBytes: number;
  canUpload?: () => boolean;
  revokeBlob: () => void;
  onPrepared: (file: File, blobUrl: string, fileName: string) => void;
  onUploaded: (logoUrl: string) => void;
  onUploadFailure: () => void;
  setUploading: (uploading: boolean) => void;
  setValidationError: (message: string) => void;
  detectChanges: () => void;
  showUploadErrorToast: () => void;
}

export async function handleModalLogoUpload(
  event: Event,
  config: ModalLogoUploadConfig,
  uploadFile: (formData: FormData) => Observable<ApiResponse<CampaignFileUploadResponse>>
): Promise<void> {
  if (config.canUpload && !config.canUpload()) {
    return;
  }

  config.setValidationError('');
  config.detectChanges();

  const prepared = await prepareLogoUploadFromEvent(
    event,
    config.maxWidth,
    config.maxHeight,
    config.maxBytes
  );
  if (prepared.status === 'noop') {
    return;
  }
  if (prepared.status === 'invalid') {
    config.setValidationError(prepared.error);
    config.detectChanges();
    return;
  }

  config.revokeBlob();
  config.onPrepared(prepared.file, prepared.blobUrl, prepared.fileName);
  config.setUploading(true);
  config.detectChanges();

  runCampaignLogoUpload(uploadFile, prepared.file, prepared.input, {
    onSuccess: (logoUrl) => config.onUploaded(logoUrl),
    onFailure: () => config.onUploadFailure(),
    setUploading: config.setUploading,
    setValidationError: config.setValidationError,
    detectChanges: config.detectChanges,
    showUploadErrorToast: config.showUploadErrorToast
  });
}

export function removeModalLogo(event: Event | undefined, clearLogo: () => void): void {
  event?.stopPropagation();
  event?.preventDefault();
  clearLogo();
}

export interface ModalLogoUploadHost {
  maxWidth: number;
  maxHeight: number;
  maxBytes: number;
  canUpload?: () => boolean;
  revokeBlob: () => void;
  assignPreparedFile: (file: File, blobUrl: string, fileName: string) => void;
  applyUploadedLogo: (logoUrl: string) => void;
  onUploadFailure: () => void;
  setUploading: (uploading: boolean) => void;
  setValidationError: (message: string) => void;
  detectChanges: () => void;
  showUploadErrorToast: () => void;
}

export function createModalLogoUploadConfig(host: ModalLogoUploadHost): ModalLogoUploadConfig {
  return {
    maxWidth: host.maxWidth,
    maxHeight: host.maxHeight,
    maxBytes: host.maxBytes,
    canUpload: host.canUpload,
    revokeBlob: host.revokeBlob,
    onPrepared: host.assignPreparedFile,
    onUploaded: host.applyUploadedLogo,
    onUploadFailure: host.onUploadFailure,
    setUploading: host.setUploading,
    setValidationError: host.setValidationError,
    detectChanges: host.detectChanges,
    showUploadErrorToast: host.showUploadErrorToast
  };
}

/** FormData field name for MMS coupon image uploads. */
export const MMS_COUPON_IMAGE_FIELD = 'mms_coupon_image_url';

export function appendMmsCouponImageToFormData(formData: FormData, file: File): void {
  formData.append(MMS_COUPON_IMAGE_FIELD, file, file.name);
}

/** FormData field name for RCS coupon image uploads. */
export const RCS_COUPON_IMAGE_FIELD = 'rcs_coupon_image_url';

export function appendRcsCouponImageToFormData(formData: FormData, file: File): void {
  formData.append(RCS_COUPON_IMAGE_FIELD, file, file.name);
}
